import React from 'react';
import classNames from 'classnames';
import { startCase } from 'lodash';

import { Counter } from '@/components';
import { NOT_DEFINED } from '@/constants';

import {
  ProductActionFavorite,
  ProductBadgeContainer,
  ProductCannabinoidProfile,
  ProductCategory,
  ProductImage,
  ProductInfo,
  ProductInfoCarousel,
  ProductLinkWrapper,
  ProductName,
  ProductPrice,
  ProductVariants,
} from '../Product.components';
import { ProductVariationProps } from '../Product';
import { useProductContext } from '../context';

import style from '../product.module.scss';
import styleVariation from './ProductCardWithInfoCentered.module.scss';

const ProductCardWithInfoCentered: React.FC<ProductVariationProps> = ({
  className,
  analyticsListName,
  isLargeCard,
  onKiosk,
  isAddToCartInKioskDisabled,
  onKioskAddClicked,
}) => {
  const { isSpecial, product, variants, weightVariant, compoundList } =
    useProductContext();

  const category = !!product?.flower_type;
  const showCompoundList = compoundList?.length > 0;

  const availableInfo = [
    category && (
      <ProductCategory
        info={product?.flower_type as string}
        key="category"
        type="default"
      />
    ),
    showCompoundList && (
      <ProductCannabinoidProfile
        badgeClass={styleVariation.variation__info_badge}
        badgeMode="default"
        compoundList={compoundList}
        initDot={category}
        key="info-product"
      />
    ),
    product?.subcategory && (
      <div className={style.info__item_conteiner} key="subcategory">
        {(category || showCompoundList) && <div className={style.info__dot} />}
        <ProductInfo
          className={styleVariation.variation__info_badge}
          info={startCase(product?.subcategory!)}
          type="default"
        />
      </div>
    ),
  ].filter(Boolean);

  return (
    <ProductLinkWrapper
      analyticsListName={analyticsListName}
      className={classNames(style.product, styleVariation.product, className)}
      onKiosk={onKiosk}
      product={product}
    >
      <ProductImage
        analyticsListName={analyticsListName}
        className={styleVariation.product__image_container}
        product={product}
      />
      <div
        className={classNames(
          style.product__body,
          styleVariation.product__body,
        )}
      >
        <ProductPrice
          className={styleVariation.product__price_align_center}
          elementVariants={<ProductVariants />}
          isOverflow={variants?.length === 1}
          isSpecial={isSpecial}
          type="column-container"
          weightVariant={weightVariant}
        />
        <ProductBadgeContainer
          className={styleVariation.product_badge__container}
          key="badge-container"
          onKiosk={onKiosk}
          product={product}
          weightVariant={weightVariant}
        />
        <div className={styleVariation.product__info}>
          <ProductName
            className={styleVariation.product__name}
            name={product?.name || NOT_DEFINED}
          />
          <ProductInfo
            className={styleVariation.variation__info_badge}
            info={product.brand_name || NOT_DEFINED}
            key="info-brand"
            type="default"
          />
        </div>
        <ProductInfoCarousel
          className={styleVariation.variation__info_carousel}
          classWrapper={styleVariation.variation__info_carousel_wrapper}
        >
          {availableInfo}
        </ProductInfoCarousel>
        <div
          aria-label="Product actions"
          className={styleVariation.actions__body}
          role="group"
        >
          <ProductActionFavorite
            className={styleVariation.favorite__button}
            product={product}
          />
        </div>
      </div>
      {onKiosk && isAddToCartInKioskDisabled ? null : (
        <div
          className={classNames(
            style.product__actions_footer_button,
            styleVariation.product__actions_footer_button,
            {
              [style.product__actions_footer_button_disabled]:
                product?.isLowStock,
              [style.product__actions_footer_button_large_card]: isLargeCard,
            },
          )}
        >
          <Counter
            actionTheme="link"
            className={classNames(style.actions__counter_button, {
              [style.actions__counter_button_large_card]: isLargeCard,
            })}
            classNameCounter={style.actions__counter_button_form}
            onCard
            onKiosk={onKiosk}
            onKioskAddClicked={onKioskAddClicked}
            product={product}
            selectedWeightVariant={weightVariant}
            showIcon
          />
        </div>
      )}
    </ProductLinkWrapper>
  );
};

export { ProductCardWithInfoCentered };
